import { useState, Dispatch, SetStateAction } from 'react';
import { first } from 'rxjs/operators';
import { IDataPackage, IDisallowedExtensionsResult } from '@vivli/shared/features/data-package/infrastructure/interface';
import { useDataPackageApiContext, useDataRequestsApiContext, useModalApiContext } from '@vivli/core/infrastructure/context';

export const useResultRequestsFileInspection = (
    onPackageSubmitted?: (pkg: IDataPackage) => void,
    externalSetSubmittingDataPackage?: Dispatch<SetStateAction<boolean>>
) => {
    const _dataPackageService = useDataPackageApiContext();
    const _dataRequestService = useDataRequestsApiContext();
    const _modalService = useModalApiContext();

    const [submittingDataPackage, setSubmittingDataPackage] = useState(false);

    const handleRestrictedFilesFound = (badFiles?: string[], forZipFiles?: boolean) => {
        const badFileTypes = formatExtensions(badFiles);
        const msg = forZipFiles
            ? `For data protection and security reasons, certain file types cannot be exported. This ZIP archive includes one or more restricted files and cannot be exported as-is.\r\n Please remove the restricted ${badFileTypes} file(s) and re-attempt export.`
            : `For data protection and security reasons, certain file types cannot be exported. Please remove the restricted ${badFileTypes} file(s) and re-attempt export.`;
        _modalService.message(msg, {
            title: 'Export Not Allowed – Restricted File Type',
            showContinueButton: true,
            // The ZIP wording carries a line break. The message modal renders into a plain div, so
            // without this the break collapses to a space and the two sentences run together.
            messageStyle: { whiteSpace: 'pre-line' }
        });
    };

    const handleUnrecognizedFilesFound = (unrecognizedFiles?: string[]) => {
        const unrecognizedFileTypes = formatExtensions(unrecognizedFiles);
        // DEV-821: a file with no extension at all arrives as an empty entry that formatExtensions
        // drops, so the list can legitimately render empty. "or no extensions" covers that case
        // without needing a second message variant for it.
        const msg = `Files with the following extensions or no extensions are not permitted on the Vivli Platform and cannot be uploaded: ${unrecognizedFileTypes}. If you believe these file types should be permitted, please contact support@vivli.org.`;
        _modalService.message(msg, {
            title: 'File Type Not Recognized',
            showContinueButton: true
        });
    };

    /**
     * Decides which message the user gets, and shows it. A file set with restricted files is a hard
     * stop, so those win when a submission has both kinds of problem - the unrecognized message
     * surfaces on the next attempt once the restricted files are gone.
     * Returns true when nothing is wrong and the caller can go on to submit.
     */
    const allFilesAllowed = (disallowedExtensions?: IDisallowedExtensionsResult, forZipFiles?: boolean): boolean => {
        if (disallowedExtensions?.forbiddenExtensions?.length > 0) {
            handleRestrictedFilesFound(disallowedExtensions.forbiddenExtensions, forZipFiles);
            return false;
        }

        if (disallowedExtensions?.unrecognizedExtensions?.length > 0) {
            handleUnrecognizedFilesFound(disallowedExtensions.unrecognizedExtensions);
            return false;
        }

        return true;
    };

    /**
     * DEV-821: the backend records a file with no extension as an empty string, so the falsy filter
     * below is deliberate - rendering it would put a bare comma or a stray separator in the message.
     * The wording in handleUnrecognizedFilesFound is what accounts for the dropped entry. Don't
     * "fix" the filter to keep empty entries.
     */
    const formatExtensions = (extensions?: string[]): string => {
        if (!extensions || extensions.length === 0) return '';
        const distinctExtensions = extensions.filter((ext) => !!ext).filter((ext, i, all) => all.indexOf(ext) === i);
        return distinctExtensions.join(', ');
    };

    const confirmAndSubmit = (dataRequestId: string, dataPackage: IDataPackage, resultRequestId: string) => {
        _modalService.confirm('Please confirm that all files have been uploaded and do not contain any individual participant data', {
            title: 'Submit Files?',
            confirmText: 'Confirm Submission',
            cancelText: 'Cancel',
            onConfirm: () => {
                setSubmittingDataPackage(true);
                if (externalSetSubmittingDataPackage) externalSetSubmittingDataPackage(true);

                _dataRequestService
                    .startHeuristics(dataRequestId, resultRequestId, dataPackage)
                    .pipe(first())
                    .subscribe(
                        () => {
                            setSubmittingDataPackage(false);
                            if (externalSetSubmittingDataPackage) externalSetSubmittingDataPackage(false);
                            onPackageSubmitted && onPackageSubmitted(dataPackage);
                        },
                        (error) => {
                            setSubmittingDataPackage(false);
                            if (externalSetSubmittingDataPackage) externalSetSubmittingDataPackage(false);
                            _dataPackageService.handleSubmitErrorWithFileCheck(error, true, 'Result Request');
                        }
                    );
            }
        });
    };

    return {
        confirmAndSubmit,
        allFilesAllowed,
        submittingDataPackage
    };
};
